/**
 * WebSocket Service
 * Real-time updates for instructor dashboard
 *
 * ---------------------------------------------------------------------------
 * Instructor terminal exec events (used by StudentTerminal.tsx)
 * ---------------------------------------------------------------------------
 *
 *  Outbound (instructor -> server):
 *    - instructor:terminal:connect    { environmentId: string }
 *        Open a shell exec session inside the student's running pod.
 *    - instructor:terminal:input      { environmentId: string, data: string }
 *        Forward raw keystrokes from the instructor's xterm to the pod stdin.
 *    - instructor:terminal:disconnect { environmentId: string }
 *        Tear down the exec stream cleanly. Does NOT stop the student pod.
 *
 *  Inbound (server -> instructor):
 *    - instructor:terminal:output     { data: string }
 *        Streamed stdout/stderr chunks from the pod.
 *    - instructor:terminal:status     { status: 'connected' | 'error',
 *                                       message?: string }
 *        Connection lifecycle / error signal for the UI status dot.
 *
 *  All keystrokes typed by the instructor MUST be audit-logged server-side
 *  (the UI shows a notice to that effect). The websocket-gateway needs to
 *  implement the handlers for these events — see report.
 * ---------------------------------------------------------------------------
 *
 * ---------------------------------------------------------------------------
 * Messaging events (used by ConversationThread + Messages page)
 * ---------------------------------------------------------------------------
 *
 *  Inbound (server -> instructor):
 *    - message:new      Full `Message` payload. Fired when either side posts a
 *                       new message in any conversation the instructor is a
 *                       party to. Receivers de-dupe by `id` (handles replay
 *                       on reconnect cleanly).
 *    - message:typing   { conversationId, senderRole, senderName }
 *                       Ephemeral. UI auto-clears the indicator after 5s of
 *                       silence per conversation.
 *    - message:read     { conversationId, messageIds: string[], readAt }
 *                       Sent when the student opens the thread and marks our
 *                       messages read; flips ✓ → ✓✓ in the bubble.
 *
 *  Outbound (instructor -> server):
 *    - message:typing   { conversationId } — throttled to 1 ping / 2s while
 *                       the composer textarea is keystroking.
 *
 *  Auto-reconnect:
 *    socket.io reconnection is enabled with effectively unlimited retries
 *    (capped only at `reconnectionDelayMax`). On `connect` we re-emit
 *    `instructor:join` so the gateway re-subscribes us to our rooms.
 * ---------------------------------------------------------------------------
 */

import { io, Socket } from 'socket.io-client';
import { getToken } from './apiClient';
import type { EnvironmentUpdateEvent, SubmissionEvent, ActivityEvent } from '../types';
import { USE_MOCK_DATA } from '../lib/mockFallback';

const WS_URL = process.env.REACT_APP_WEBSOCKET_GATEWAY_URL || 'http://localhost:3005';

type EventCallback<T> = (data: T) => void;

class WebSocketService {
  private socket: Socket | null = null;
  private listeners: Map<string, Set<EventCallback<unknown>>> = new Map();
  private dynamicHitlHandlers: Map<string, EventCallback<unknown>> = new Map();
  private reconnectAttempts = 0;
  // Effectively unlimited retries — transient backend outages shouldn't
  // permanently break the messaging UI. socket.io applies exponential
  // backoff up to `reconnectionDelayMax`.
  private maxReconnectAttempts = Infinity;
  private reconnectDelay = 1000;
  private reconnectDelayMax = 10_000;
  // Mock-mode terminal session bookkeeping
  private mockTerminalBuffers: Map<string, string> = new Map();

  /**
   * Connect to WebSocket server
   */
  connect(): void {
    if (this.socket?.connected) {
      return;
    }

    const token = getToken();
    if (!token) {
      console.warn('No auth token, skipping WebSocket connection');
      return;
    }

    this.socket = io(WS_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay,
      reconnectionDelayMax: this.reconnectDelayMax,
    });

    this.socket.on('connect', () => {
      console.log('WebSocket connected');
      this.reconnectAttempts = 0;
      // Join instructor room for broadcast events
      this.socket?.emit('instructor:join');
      this.dispatch('connect', { connected: true });
    });

    this.socket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
      this.dispatch('disconnect', { reason });
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      this.reconnectAttempts++;
    });

    // Environment updates
    this.socket.on('environment:update', (data: EnvironmentUpdateEvent) => {
      this.dispatch('environment:update', data);
    });

    this.socket.on('environment:created', (data: EnvironmentUpdateEvent) => {
      this.dispatch('environment:created', data);
    });

    this.socket.on('environment:deleted', (data: { environmentId: string; userId: string }) => {
      this.dispatch('environment:deleted', data);
    });

    // Submission events
    this.socket.on('submission:new', (data: SubmissionEvent) => {
      this.dispatch('submission:new', data);
    });

    this.socket.on('submission:graded', (data: SubmissionEvent) => {
      this.dispatch('submission:graded', data);
    });

    // Activity events
    this.socket.on('activity:new', (data: ActivityEvent) => {
      this.dispatch('activity:new', data);
    });

    // Stats updates
    this.socket.on('stats:update', (data: unknown) => {
      this.dispatch('stats:update', data);
    });

    // Instructor terminal exec stream
    this.socket.on('instructor:terminal:output', (data: { data: string }) => {
      this.dispatch('instructor:terminal:output', data);
    });

    this.socket.on('instructor:terminal:status', (data: { status: string; message?: string }) => {
      this.dispatch('instructor:terminal:status', data);
    });

    // Messaging events (see header docstring for payload shapes)
    this.socket.on('message:new', (data: unknown) => {
      this.dispatch('message:new', data);
    });

    this.socket.on('message:typing', (data: unknown) => {
      this.dispatch('message:typing', data);
    });

    this.socket.on('message:read', (data: unknown) => {
      this.dispatch('message:read', data);
    });

    // HITL writes are HTTP-only. These are canonical rows/events fanned out
    // by the gateway / session-manager (broadcastToInstructors) after the
    // database transaction commits. Register the bounded set of static event
    // names so HitlQueue/HitlSession subscriptions fire.
    for (const event of [
      'hitl:session:assigned',
      'hitl:session:message',
      'hitl:session:annotation',
      'hitl:session:resolved',
      'hitl:queue:updated',
    ]) {
      this.socket.on(event, (data: unknown) => this.dispatch(event, data));
    }

    // Presence and IDE peek retain session-specific event names
    // (hitl:session:{id}:presence, hitl:session:{id}:ide-peek). Bind only
    // the two bounded HITL shapes requested by an active session view.
    this.listeners.forEach((_callbacks, event) => this.bindDynamicHitlEvent(event));
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.dynamicHitlHandlers.clear();
    this.listeners.clear();
  }

  /**
   * Subscribe to an event
   */
  on<T>(event: string, callback: EventCallback<T>): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback as EventCallback<unknown>);
    this.bindDynamicHitlEvent(event);

    // Return unsubscribe function
    return () => {
      this.listeners.get(event)?.delete(callback as EventCallback<unknown>);
      if (this.listeners.get(event)?.size === 0) {
        const handler = this.dynamicHitlHandlers.get(event);
        if (handler) {
          this.socket?.off(event, handler);
          this.dynamicHitlHandlers.delete(event);
        }
      }
    };
  }

  private bindDynamicHitlEvent(event: string): void {
    const dynamicHitlEvent =
      /^hitl:session:[0-9a-f-]{36}:(presence|ide-peek)$/.test(event);
    if (!dynamicHitlEvent || !this.socket || this.dynamicHitlHandlers.has(event)) {
      return;
    }
    const handler: EventCallback<unknown> = (data) => this.dispatch(event, data);
    this.dynamicHitlHandlers.set(event, handler);
    this.socket.on(event, handler);
  }

  /**
   * Dispatch event to local listeners (internal fan-out from socket -> hooks).
   */
  private dispatch(event: string, data: unknown): void {
    this.listeners.get(event)?.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in WebSocket event handler for ${event}:`, error);
      }
    });
  }

  /**
   * Send event to the server. In mock-data mode the terminal-related events
   * are intercepted and simulated locally so the UI is exercisable without
   * the websocket-gateway running.
   */
  emit(event: string, data: unknown): void {
    if (USE_MOCK_DATA && event.startsWith('instructor:terminal:')) {
      this.handleMockTerminal(event, data);
      return;
    }

    if (this.socket?.connected) {
      this.socket.emit(event, data);
    } else {
      console.warn('WebSocket not connected, cannot send event:', event);
    }
  }

  emitWithAck<T>(event: string, data: unknown, timeoutMs = 5_000): Promise<T> {
    if (!this.socket?.connected) {
      return Promise.reject(new Error('WebSocket is not connected'));
    }
    return new Promise<T>((resolve, reject) => {
      let settled = false;
      const timeout = setTimeout(() => {
        if (!settled) {
          settled = true;
          reject(new Error('WebSocket acknowledgement timed out'));
        }
      }, timeoutMs);
      this.socket!.emit(event, data, (response: T) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        resolve(response);
      });
    });
  }

  /**
   * Backwards-compatible alias — older call sites use `send`.
   */
  send(event: string, data: unknown): void {
    this.emit(event, data);
  }

  /**
   * Simulate a tiny shell for mock-mode demos. Supports a handful of
   * commands (ls, pwd, cat <file>, clear, echo) so instructors can poke
   * at the UI without a real pod.
   */
  private handleMockTerminal(event: string, data: unknown): void {
    const payload = (data ?? {}) as { environmentId?: string; data?: string };
    const envId = payload.environmentId ?? 'mock';

    if (event === 'instructor:terminal:connect') {
      this.mockTerminalBuffers.set(envId, '');
      // Fire status + an initial banner asynchronously so subscribers
      // mounted in the same tick still receive the events.
      setTimeout(() => {
        this.dispatch('instructor:terminal:status', { status: 'connected' });
        this.dispatch('instructor:terminal:output', {
          data: '\x1b[32mConnected to mock environment\x1b[0m\r\n$ ',
        });
      }, 50);
      return;
    }

    if (event === 'instructor:terminal:disconnect') {
      this.mockTerminalBuffers.delete(envId);
      this.dispatch('instructor:terminal:status', { status: 'disconnected' });
      return;
    }

    if (event === 'instructor:terminal:input') {
      const chunk = payload.data ?? '';
      // Echo what was typed (real ttys do this; the mock shell isn't a pty)
      this.dispatch('instructor:terminal:output', { data: chunk });

      let buf = this.mockTerminalBuffers.get(envId) ?? '';
      for (const ch of chunk) {
        if (ch === '\r' || ch === '\n') {
          this.dispatch('instructor:terminal:output', { data: '\r\n' });
          const output = this.runMockCommand(buf.trim());
          if (output) {
            this.dispatch('instructor:terminal:output', { data: output + '\r\n' });
          }
          this.dispatch('instructor:terminal:output', { data: '$ ' });
          buf = '';
        } else if (ch === '\x7f' || ch === '\b') {
          // Backspace handling — trim the buffer and visually erase
          if (buf.length > 0) {
            buf = buf.slice(0, -1);
            this.dispatch('instructor:terminal:output', { data: '\b \b' });
          }
        } else {
          buf += ch;
        }
      }
      this.mockTerminalBuffers.set(envId, buf);
    }
  }

  private runMockCommand(cmd: string): string {
    if (!cmd) return '';
    if (cmd === 'ls') {
      return 'lab.py  README.md  data/  tests/';
    }
    if (cmd === 'pwd') {
      return '/home/student/workspace';
    }
    if (cmd.startsWith('cat ')) {
      const file = cmd.slice(4).trim();
      if (file === 'README.md') {
        return '# Lab\nThis is a mock README rendered by the simulated shell.';
      }
      return `cat: ${file}: No such file or directory`;
    }
    if (cmd === 'clear') {
      return '\x1b[2J\x1b[H';
    }
    if (cmd.startsWith('echo ')) {
      return cmd.slice(5);
    }
    return `${cmd.split(' ')[0]}: command not found (mock shell)`;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }
}

// Export singleton instance
export const wsService = new WebSocketService();

// Export hook for using WebSocket in components
export function useWebSocket() {
  return wsService;
}
